import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Drawer,
  FormControlLabel,
  Grid,
  Paper,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Alert, Skeleton } from "@material-ui/lab";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";

import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";
import usePlans from "../../hooks/usePlans";
import useCompanies from "../../hooks/useCompanies";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: { width: "100%", padding: theme.spacing(1, 0) },
  header: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: theme.spacing(1), marginBottom: theme.spacing(2) },
  title: { fontWeight: 700, fontSize: "1.05rem" },
  sub: { color: theme.palette.text.secondary, fontSize: "0.82rem" },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: theme.spacing(2),
  },
  card: { padding: theme.spacing(2), borderRadius: 12, display: "flex", flexDirection: "column", gap: theme.spacing(1.5) },
  price: { fontSize: "1.6rem", fontWeight: 700, fontVariantNumeric: "tabular-nums" },
  priceSmall: { fontSize: "0.8rem", fontWeight: 400, color: theme.palette.text.secondary },
  limits: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: theme.spacing(0.75), textAlign: "center" },
  limit: { backgroundColor: theme.palette.action.hover, borderRadius: 8, padding: theme.spacing(0.75, 0.25) },
  limitValue: { fontWeight: 700, fontSize: "1.05rem" },
  limitLabel: { fontSize: "0.68rem", color: theme.palette.text.secondary },
  features: { display: "flex", flexWrap: "wrap", gap: theme.spacing(0.5) },
  featOn: { fontWeight: 600, fontSize: "0.7rem" },
  featOff: { fontSize: "0.7rem", textDecoration: "line-through", opacity: 0.6 },
  actions: { display: "flex", alignItems: "center", gap: theme.spacing(1) },
  drawerPaper: { width: "min(520px, 100%)", padding: theme.spacing(2.5) },
  formSection: { fontWeight: 700, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.06em", color: theme.palette.text.secondary, margin: theme.spacing(2, 0, 0.5) },
  footer: { display: "flex", gap: theme.spacing(1), marginTop: theme.spacing(3), flexWrap: "wrap" },
}));

const FEATURES = [
  { key: "useCampaigns", label: "Campanhas" },
  { key: "useSchedules", label: "Agendamentos" },
  { key: "useInternalChat", label: "Chat interno" },
  { key: "useExternalApi", label: "API externa" },
  { key: "useKanban", label: "Kanban" },
  { key: "useOpenAi", label: "Assistente de IA" },
  { key: "useIntegrations", label: "Integrações" },
];

// Créditos de campanha. Vazio quer dizer "sem controle" e precisa chegar ao
// backend como null — 0 tem outro sentido na cota mensal: controle ligado, sem
// cota, valendo só o saldo avulso da empresa.
const CREDIT_FIELDS = [
  {
    key: "campaignMonthlyCredits",
    label: "Créditos por mês",
    help: "Vazio = sem controle de créditos. 0 = só o saldo avulso.",
  },
  { key: "campaignMaxContacts", label: "Máx. contatos por campanha", help: "Vazio ou 0 = sem limite." },
  { key: "campaignMaxConcurrent", label: "Máx. campanhas simultâneas", help: "Vazio ou 0 = sem limite." },
];

const toNullableInt = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

const EMPTY_PLAN = {
  name: "",
  users: 1,
  connections: 1,
  queues: 1,
  value: 0,
  useCampaigns: true,
  useSchedules: true,
  useInternalChat: true,
  useExternalApi: true,
  useKanban: true,
  useOpenAi: true,
  useIntegrations: true,
  isPublic: true,
  campaignMonthlyCredits: "",
  campaignMaxContacts: "",
  campaignMaxConcurrent: "",
};

const PlanSchema = Yup.object().shape({
  name: Yup.string().trim().min(2, "Nome precisa ter ao menos 2 caracteres").max(60, "Máximo de 60 caracteres").required("Informe o nome"),
  value: Yup.number().typeError("Informe um valor").min(0, "Não pode ser negativo").required("Informe o valor"),
  users: Yup.number().typeError("Informe um número").integer("Use número inteiro").min(0, "Não pode ser negativo"),
  connections: Yup.number().typeError("Informe um número").integer("Use número inteiro").min(0, "Não pode ser negativo"),
  queues: Yup.number().typeError("Informe um número").integer("Use número inteiro").min(0, "Não pode ser negativo"),
});

const brl = (value) =>
  Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

function PlanCard({ plan, companiesCount, onEdit }) {
  const classes = useStyles();
  return (
    <Paper variant="outlined" className={classes.card}>
      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        <Typography style={{ fontWeight: 700, fontSize: "1.05rem" }}>{plan.name}</Typography>
        {plan.isPublic === false && (
          <Tooltip title="Não aparece no cadastro público">
            <Chip size="small" variant="outlined" label="Personalizado" />
          </Tooltip>
        )}
        <Box flex={1} />
        <Chip
          size="small"
          color={companiesCount ? "primary" : "default"}
          variant={companiesCount ? "default" : "outlined"}
          label={companiesCount === 1 ? "1 empresa" : `${companiesCount} empresas`}
        />
      </Box>
      <Typography className={classes.price}>
        {brl(plan.value)} <span className={classes.priceSmall}>/mês</span>
      </Typography>
      <div className={classes.limits}>
        {[
          ["users", "usuários"],
          ["connections", "conexões"],
          ["queues", "departam."],
        ].map(([key, label]) => (
          <div key={key} className={classes.limit}>
            <Typography className={classes.limitValue}>{plan[key] || 0}</Typography>
            <Typography className={classes.limitLabel}>{label}</Typography>
          </div>
        ))}
      </div>
      <div className={classes.features}>
        {FEATURES.map((f) => {
          const on = plan[f.key] !== false;
          return (
            <Chip
              key={f.key}
              size="small"
              label={f.label}
              color={on ? "primary" : "default"}
              variant={on ? "default" : "outlined"}
              className={on ? classes.featOn : classes.featOff}
            />
          );
        })}
      </div>
      {plan.useCampaigns !== false && (
        <Typography variant="caption" color="textSecondary" component="div" style={{ marginTop: 8 }}>
          {plan.campaignMonthlyCredits == null
            ? "Campanhas sem controle de créditos"
            : `Campanhas: ${Number(plan.campaignMonthlyCredits).toLocaleString("pt-BR")} créditos/mês`}
        </Typography>
      )}
      <div className={classes.actions}>
        <Button size="small" variant="outlined" onClick={() => onEdit(plan)}>Editar</Button>
      </div>
    </Paper>
  );
}

function PlanForm({ initialValue, companiesCount, loading, onSubmit, onCancel, onDelete }) {
  const classes = useStyles();
  const isEditing = initialValue.id !== undefined;
  const inUse = companiesCount > 0;

  return (
    <Formik
      enableReinitialize
      initialValues={initialValue}
      validationSchema={PlanSchema}
      onSubmit={(values) => onSubmit(values)}
    >
      {({ values, setFieldValue, handleChange, handleBlur, touched, errors }) => (
        <Form>
          <Typography className={classes.title}>{isEditing ? `Editar plano — ${initialValue.name}` : "Novo plano"}</Typography>

          {isEditing && inUse && (
            <Alert severity="info" style={{ marginTop: 12 }}>
              {companiesCount === 1 ? "1 empresa usa" : `${companiesCount} empresas usam`} este plano. Mudanças de limite e de recursos valem para elas na hora.
            </Alert>
          )}

          <Typography className={classes.formSection}>Plano</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={7}>
              <TextField
                name="name"
                label="Nome"
                variant="outlined"
                margin="dense"
                fullWidth
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                name="value"
                label="Valor mensal (R$)"
                type="number"
                inputProps={{ min: 0, step: "0.01" }}
                variant="outlined"
                margin="dense"
                fullWidth
                value={values.value}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.value && Boolean(errors.value)}
                helperText={touched.value && errors.value}
              />
            </Grid>
          </Grid>

          <Typography className={classes.formSection}>Limites</Typography>
          <Grid container spacing={2}>
            {[
              ["users", "Usuários"],
              ["connections", "Conexões"],
              ["queues", "Departamentos"],
            ].map(([name, label]) => (
              <Grid item xs={12} sm={4} key={name}>
                <TextField
                  name={name}
                  label={label}
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  value={values[name]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched[name] && Boolean(errors[name])}
                  helperText={touched[name] && errors[name]}
                />
              </Grid>
            ))}
          </Grid>
          <Typography variant="caption" color="textSecondary">
            Se o limite baixar, o que a empresa já tem continua; só impede criar novos.
          </Typography>

          <Typography className={classes.formSection}>Recursos incluídos</Typography>
          <Grid container>
            {FEATURES.map((f) => (
              <Grid item xs={12} sm={6} key={f.key}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={values[f.key] !== false}
                      onChange={(e) => setFieldValue(f.key, e.target.checked)}
                    />
                  }
                  label={f.label}
                />
              </Grid>
            ))}
          </Grid>

          {values.useCampaigns !== false && (
            <>
              <Typography className={classes.formSection}>Créditos de campanha</Typography>
              <Grid container spacing={2}>
                {CREDIT_FIELDS.map((f) => (
                  <Grid item xs={12} sm={4} key={f.key}>
                    <TextField
                      name={f.key}
                      label={f.label}
                      type="number"
                      inputProps={{ min: 0, step: 1 }}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      value={values[f.key] ?? ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      helperText={f.help}
                    />
                  </Grid>
                ))}
              </Grid>
              <Typography variant="caption" color="textSecondary">
                1 crédito = 1 mensagem enviada. A cota renova a cada ciclo; créditos avulsos são lançados no cadastro da
                empresa e não expiram.
              </Typography>
            </>
          )}

          <Typography className={classes.formSection}>Visibilidade</Typography>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={values.isPublic !== false}
                onChange={(e) => setFieldValue("isPublic", e.target.checked)}
              />
            }
            label="Exibir no cadastro público"
          />
          <Typography variant="caption" color="textSecondary" component="div">
            Desmarque para planos negociados com um cliente: o plano continua
            disponível em Empresas, mas some da tela de cadastro.
          </Typography>

          <div className={classes.footer}>
            {isEditing && (
              <Tooltip title={inUse ? "Mude as empresas para outro plano antes de excluir." : ""}>
                <span>
                  <Button color="secondary" variant="outlined" disabled={inUse || loading} onClick={() => onDelete(initialValue)}>
                    Excluir
                  </Button>
                </span>
              </Tooltip>
            )}
            <Box flex={1} />
            <Button variant="outlined" onClick={onCancel}>Cancelar</Button>
            <ButtonWithSpinner loading={loading} type="submit" variant="contained" color="primary">
              {isEditing ? "Salvar" : "Cadastrar"}
            </ButtonWithSpinner>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export default function PlansManager() {
  const classes = useStyles();
  const { list, save, update, remove } = usePlans();
  const { list: listCompanies } = useCompanies();

  const [plans, setPlans] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState(EMPTY_PLAN);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = async () => {
    setListLoading(true);
    try {
      const [planList, companyList] = await Promise.all([list(), listCompanies()]);
      setPlans(Array.isArray(planList) ? planList : []);
      setCompanies(Array.isArray(companyList) ? companyList : []);
    } catch (e) {
      toastError(e);
    }
    setListLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countByPlan = useMemo(() => {
    const map = {};
    companies.forEach((c) => {
      map[c.planId] = (map[c.planId] || 0) + 1;
    });
    return map;
  }, [companies]);

  const close = () => {
    setDrawerOpen(false);
    setRecord(EMPTY_PLAN);
  };

  const handleNew = () => {
    setRecord(EMPTY_PLAN);
    setDrawerOpen(true);
  };

  const handleEdit = (plan) => {
    const flags = {};
    FEATURES.forEach((f) => {
      flags[f.key] = plan[f.key] !== false;
    });
    setRecord({
      id: plan.id,
      name: plan.name || "",
      users: plan.users || 0,
      connections: plan.connections || 0,
      queues: plan.queues || 0,
      value: Number(plan.value) || 0,
      ...flags,
      isPublic: plan.isPublic !== false,
      campaignMonthlyCredits: plan.campaignMonthlyCredits ?? "",
      campaignMaxContacts: plan.campaignMaxContacts ?? "",
      campaignMaxConcurrent: plan.campaignMaxConcurrent ?? "",
    });
    setDrawerOpen(true);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        name: values.name.trim(),
        value: Number(values.value),
        users: Number(values.users),
        connections: Number(values.connections),
        queues: Number(values.queues),
        campaignMonthlyCredits: toNullableInt(values.campaignMonthlyCredits),
        campaignMaxContacts: toNullableInt(values.campaignMaxContacts),
        campaignMaxConcurrent: toNullableInt(values.campaignMaxConcurrent),
      };
      if (payload.id !== undefined) {
        await update(payload);
      } else {
        await save(payload);
      }
      await load();
      close();
      toast.success("Plano salvo.");
    } catch (e) {
      toastError(e);
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await remove(record.id);
      setConfirmDelete(false);
      await load();
      close();
      toast.success("Plano excluído.");
    } catch (e) {
      setConfirmDelete(false);
      toastError(e);
    }
    setLoading(false);
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Box>
          <Typography className={classes.title}>Planos</Typography>
          <Typography className={classes.sub}>
            Limites e recursos de cada plano. Alterar um plano vale para todas as empresas que o usam.
          </Typography>
        </Box>
        <Box flex={1} />
        <Button variant="contained" color="primary" onClick={handleNew}>+ Novo plano</Button>
      </div>

      <div className={classes.grid}>
        {listLoading
          ? [0, 1, 2].map((i) => <Skeleton key={i} variant="rect" height={260} style={{ borderRadius: 12 }} />)
          : plans.map((plan) => (
              <PlanCard key={plan.id} plan={plan} companiesCount={countByPlan[plan.id] || 0} onEdit={handleEdit} />
            ))}
      </div>
      {!listLoading && plans.length === 0 && (
        <Typography color="textSecondary" style={{ padding: 24, textAlign: "center" }}>
          Nenhum plano cadastrado. Use "Novo plano" para criar o primeiro.
        </Typography>
      )}

      <Drawer anchor="right" open={drawerOpen} onClose={close} classes={{ paper: classes.drawerPaper }}>
        <PlanForm
          initialValue={record}
          companiesCount={record.id ? countByPlan[record.id] || 0 : 0}
          loading={loading}
          onSubmit={handleSubmit}
          onCancel={close}
          onDelete={() => setConfirmDelete(true)}
        />
      </Drawer>

      <ConfirmationModal
        title="Excluir plano"
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
      >
        {`Excluir o plano "${record.name || ""}"? Essa ação não tem volta.`}
      </ConfirmationModal>
    </div>
  );
}
